import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { IConversationData } from '../../interfaces/conversations.interfaces';

import { EMessageContent } from '../../enums/conversation.enums';

@Injectable({
  providedIn: 'root'
})
export class ChatListService {
  private conversations: IConversationData[] = [];

  public selectedConversationId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public isChatsFullScreen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isChatsFullScreen$ = this.isChatsFullScreen.asObservable();
  private conversationsSubject = new BehaviorSubject<IConversationData[]>(this.conversations);
  public conversations$ = this.conversationsSubject.asObservable();

  setConversations(conversations: IConversationData[]): void {
    const processedConversations = conversations.map(conv => ({
      ...conv,
      last_message: this.processLastMessage(conv.last_message, conv.creator_name)
    }));
    this.conversations = this.sortByLastMessage(processedConversations);
    this.conversationsSubject.next(this.conversations);
  }

  selectConversation(conversationId: string): void {
    this.conversations = this.conversations.map(conv => ({
      ...conv,
      isSelected: conv.conversation_id === conversationId
    }));
    this.conversationsSubject.next(this.conversations);
  }

  getConversationById(conversationId: string): IConversationData | undefined {
    return this.conversations.find(conv => conv.conversation_id === conversationId);
  }

  addConversation(conversation: IConversationData): void {
    const existingIndex = this.conversations.findIndex(
      conv => conv.conversation_id === conversation.conversation_id
    );

    if (existingIndex !== -1) {
      // Update existing conversation
      this.conversations[existingIndex] = {
        ...this.conversations[existingIndex],
        ...conversation
      };
    } else {
      // Add new conversation
      this.conversations = [conversation, ...this.conversations];
    }

    this.conversations = this.sortByLastMessage(this.conversations);
    this.conversationsSubject.next(this.conversations);
  }

  toggleTypingIndicator(conversationId: string, isTyping: boolean): void {
    const conversation = this.conversations.find(conv => conv.conversation_id === conversationId);

    if (conversation) {
      conversation.isTyping = isTyping;
      this.conversationsSubject.next([...this.conversations]);
    }
  }

  updateLastMessage(conversationId: string, message: string): void {
    const timestamp = Date.now();
    const existingIndex = this.conversations.findIndex(c => c.conversation_id === conversationId);

    if (existingIndex !== -1) {
      // Update existing conversation
      const updatedConversation = {
        ...this.conversations[existingIndex],
        last_message: this.processLastMessage(
          message,
          this.conversations[existingIndex].creator_name
        ),
        last_message_timestamp: timestamp,
        isTyping: false
      };

      this.conversations[existingIndex] = updatedConversation;
    } else {
      // Create new conversation with minimal required data
      const newConversation: IConversationData = {
        conversation_id: conversationId,
        creator_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        creator_name: 'Creator',
        last_message: this.processLastMessage(message, 'Creator'),
        last_message_timestamp: timestamp,
        thumbnail_image: '',
        isSelected: false,
        isTyping: false,
        messages: [],
        subscribed: false,
        chat_state: {
          isInitialized: false,
          lastConnected: Date.now(),
          isTyping: false,
          lastTypingTimestamp: 0
        }
      };

      this.conversations = [newConversation, ...this.conversations];
    }

    this.conversations = this.sortByLastMessage(this.conversations);
    this.conversationsSubject.next(this.conversations);
  }

  private sortByLastMessage(conversations: IConversationData[]): IConversationData[] {
    return conversations.sort((a, b) => b.last_message_timestamp - a.last_message_timestamp);
  }

  private processLastMessage(lastMessage: string, creatorName: string): string {
    if (lastMessage && lastMessage.includes(EMessageContent.GENERATING_IMAGE)) {
      return `${creatorName} shared a media`;
    }
    return lastMessage;
  }
}
